// Maps each blog post slug to its card image AND that image's alt text.
//
// Kept separate from blogPosts.js so the article content and the artwork can be
// edited independently — posts are written by the SEO team, images are produced
// by scripts/gen-images.mjs. The `file` keys here must match the `slug` values
// in blogPosts.js, and the filenames must match the targets in the generator
// (with .png swapped for .webp, since only the WebP is served).
//
// A slug with no entry, or an entry whose file has not been generated yet, is
// harmless: MarketingArt renders nothing when the file 404s, so the card simply
// appears without art.
//
// WHY ALT TEXT LIVES HERE: it did not exist. BlogIndex rendered every card
// image with no alt prop, which MarketingArt defaults to alt="" plus
// role="presentation" — correct for a decorative banner, wrong for eight images
// that are the visual entry point to every article in the library. That is a
// straight accessibility defect first and a missed image-search surface second.
//
// Each string below describes what is actually in the frame (compare the
// prompts in scripts/gen-images.mjs) and only then names the subject it stands
// for. Alt text that describes the article instead of the picture is keyword
// stuffing — it fails the blind-user test, which is the only test that matters
// here. If the artwork is ever regenerated with a different composition, these
// strings have to change with it.
//
// FILENAMES carry the keyword rather than an internal label — "blog-suspended"
// told a crawler nothing, "form-2290-suspended-vehicle-category-w" describes
// the subject. Renamed 2026-08-14, which is the cheapest this will ever be:
// nothing links to these files externally yet and no image-search equity has
// accrued. Renaming later means breaking whatever has. Each name must match the
// target key in scripts/gen-images.mjs.
//
// AWAITING ART (D8 — image generation is unfunded, and generating costs money):
//   what-is-form-2290
//   form-2290-late-filing-penalties
//   form-2290-new-vehicle-partial-period
// Prompts for all three are already in gen-images.mjs. They are deliberately
// absent from the map below rather than pointing at files that do not exist —
// BlogIndex renders the aspect-ratio box from the map entry, so a broken entry
// would leave an empty grey rectangle on the card where MarketingArt 404s.
// Once the images are generated, add the three entries and the art appears.
export const BLOG_IMAGES = {
  "form-2290-due-date-2026-2027": {
    file: "form-2290-due-dates.webp",
    alt: "A wall calendar and a set of keys hanging in a truck cab, marking the Form 2290 filing deadline",
  },
  "stamped-schedule-1-proof-of-payment": {
    file: "form-2290-stamped-schedule-1.webp",
    alt: "A rubber stamp and ink pad resting beside a printed sheet on a desk, standing in for the IRS stamped Schedule 1",
  },
  "form-2290-vin-correction": {
    file: "form-2290-vin-correction.webp",
    alt: "Close-up of a blank metal identification plate riveted to a truck door frame, where a Form 2290 VIN correction starts",
  },
  "form-2290-amendments": {
    file: "form-2290-amendment-weight-increase.webp",
    alt: "A loaded dump truck tipping gravel on a worksite, illustrating the taxable gross weight increase that triggers a Form 2290 amendment",
  },
  "suspended-vehicles-category-w": {
    file: "form-2290-suspended-vehicle-category-w.webp",
    alt: "A semi truck parked alone at the edge of an empty field, illustrating a suspended category W vehicle driven under 5,000 miles",
  },
  "form-2290-credits-and-refunds": {
    file: "form-2290-credits-and-refunds.webp",
    alt: "A blank cheque slip, truck keys and a small stack of coins on a desk, representing a Form 2290 credit or refund",
  },
  "form-2290-weight-categories-and-rates": {
    file: "form-2290-weight-categories.webp",
    alt: "Five commercial vehicles lined up from a small van to a heavy semi tractor, showing the Form 2290 taxable gross weight categories",
  },
  "bulk-form-2290-filing-for-fleets": {
    file: "bulk-form-2290-filing-fleets.webp",
    alt: "Aerial view of a depot lot filled with neat rows of identical trailers, representing a bulk Form 2290 filing for a whole fleet",
  },
};

/** Returns { file, alt } for a slug, or null when the post has no artwork. */
export const blogImage = (slug) => BLOG_IMAGES[slug] || null;
